/**
 * Turning the authentication codes into words.
 *
 * Nothing here renders and nothing holds state, matching `notes/noteDisplay.ts`: the login
 * screen reads these, so the mapping from a code to a message is testable without a DOM and
 * the wording is reviewable in one place.
 *
 * **Every code gets a message.** {@link loginRefusalMessage} is exhaustive over
 * {@link LoginRefusalCode} by construction, so a code added on the server and typed into
 * `LOGIN_REFUSAL_CODES` fails the typecheck here until somebody has written the wording.
 * The alternative — a default branch — is a blank message on the one screen where a
 * clinician cannot get any further.
 */

import type { TFunction } from 'i18next'

import type { LoginRefusalCode, Session } from './authApi'

/**
 * The lookup the login screen and the app bar need.
 *
 * `common` first, matching the other display modules. `auth` holds the login screen's own
 * chrome and the refusals; the banner and the product name stay in `common`, because the
 * banner is on every screen from this one onwards and there must be exactly one wording of
 * it (PHASE-0.md §2.1).
 */
export type AuthTranslate = TFunction<['common', 'auth']>

/**
 * Why a login was refused.
 *
 * One code, so one message, and the message says nothing about *which* part was wrong. That
 * is not vagueness for its own sake: the server cannot distinguish them in the response
 * without telling a caller which usernames exist on the box, and a clinician's remedy is
 * the same either way.
 *
 * `UNKNOWN` is a 401 whose body did not parse. Worded as a refusal rather than as a fault,
 * because the login did not succeed and that is the part the clinician needs.
 */
export function loginRefusalMessage(
  code: LoginRefusalCode | 'UNKNOWN',
  t: AuthTranslate,
): string {
  switch (code) {
    case 'INVALID_CREDENTIALS':
      return t('auth:refusal.invalidCredentials')
    case 'UNKNOWN':
      return t('auth:refusal.unknown')
  }
}

/** The box did not answer. Names the on-call technician's job, not the clinician's. */
export function authUnavailableMessage(t: AuthTranslate): string {
  return t('auth:refusal.unavailable')
}

/**
 * A role's label, from the code.
 *
 * The code and not the stored title, because the label has to follow the interface language
 * (DECISIONS.md #35) and `role_title` is Swedish record content that is shown as stored.
 *
 * Falls back to the code itself for a role with no catalogue entry, which is exactly the
 * case phase 0's gate refuses: the role vocabulary is not settled, and `administrator`
 * arriving from a later migration should read as an untranslated role rather than as a blank
 * space where the signed-in clinician's role belongs.
 */
export function roleLabel(roleCode: string, t: AuthTranslate): string {
  return t(`auth:role.${roleCode}` as 'auth:role.physician', { defaultValue: roleCode })
}

/**
 * How the signed-in clinician is named in the app bar.
 *
 * Name and translated role. The stored Swedish title is deliberately not here: on this line
 * the reader is identifying themselves, not reading the record, and two role words side by
 * side is noise. `role_title` appears where the record's own wording is what matters — on a
 * signature, in a note's author line.
 */
export function signedInAs(session: Session, t: AuthTranslate): string {
  return t('auth:signedInAs', {
    name: session.displayName,
    role: roleLabel(session.roleCode, t),
  })
}

/**
 * Why the note form is not being offered.
 *
 * Shown in place of the form when {@link Session.canDocument} is false, so that a role which
 * may not document sees a reason rather than a screen with something missing from it. Names
 * the role, because "you may not" without "as a…" reads as a fault in the system.
 *
 * The same code the server refuses a write with — `ROLE_MAY_NOT_DOCUMENT` — so the wording a
 * clinician sees is the same whether the form was withheld or the write was refused.
 */
export function mayNotDocumentMessage(session: Session, t: AuthTranslate): string {
  return t('auth:refusal.roleMayNotDocument', {
    role: roleLabel(session.roleCode, t),
  })
}
